import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useUpdateSupplierDetails } from "../../../../api/supplier";
import { THEME } from "../../../../config/constants";
import { Supplier, SupplierEditModalProps, SupplierEditViewModel } from "../types/supplierEditView.types";

/**
 * Ports the exact state/submit logic of the legacy `SupplierEditModal`
 * (src/screens/supplier/supplierEdit/SupplierEditModal.tsx) — same
 * defaultValues/values re-init on `supplier` change, same trimming of
 * name/contact/email, same attachment of `supplier_country`,
 * `is_supplier_as_user` and `theme`, same stripping of the read-only
 * `ingredients`/`products`/`supplierIngredientDetails` fields before the
 * PUT. The only new behavior is `canSave`, a live watch on the two
 * required fields that gates the Save button — the underlying
 * required/regex field validation (see `SupplierInfoCard`) is unchanged.
 */
export const useSupplierEditViewModel = ({
  closeModal,
  onSuccessEdit,
  supplier
}: Pick<SupplierEditModalProps, "closeModal" | "onSuccessEdit" | "supplier">): SupplierEditViewModel => {
  const [isSupplierAsUser, setIsSupplierAsUser] = useState<boolean>(false);
  const [country, setCountry] = useState<number>(0);

  const values = useMemo(() => {
    setIsSupplierAsUser(supplier?.is_supplier_as_user || false);
    setCountry(supplier?.supplier_country || 0);
    return supplier?.supplier_contact_name ? supplier : { ...supplier, supplier_contact_name: "" };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplier]);

  const methods = useForm<Supplier>({
    mode: "all",
    defaultValues: values,
    values
  });

  const { handleSubmit, reset, watch } = methods;

  const supplierNameValue = watch("supplier_name");
  const supplierContactNameValue = watch("supplier_contact_name");
  const canSave = Boolean(supplierNameValue?.trim()) && Boolean(supplierContactNameValue?.trim());

  const { mutate: onUpdateSupplier, isLoading: isSaving } = useUpdateSupplierDetails({
    onError: () => {
      closeModal();
    },
    onSuccess: () => {
      closeModal();
      onSuccessEdit();
      reset();
    }
  });

  const onSubmit = (data: Supplier) => {
    const payload: Supplier = {
      ...data,
      supplier_name: data.supplier_name?.trim(),
      supplier_contact_name: data.supplier_contact_name?.trim(),
      supplier_email: data.supplier_email?.trim(),
      supplier_country: country,
      is_supplier_as_user: isSupplierAsUser,
      theme: THEME
    };
    delete payload.supplierIngredientDetails;
    delete payload.products;
    delete payload.ingredients;
    onUpdateSupplier(payload);
  };

  const onCancel = () => {
    closeModal();
    reset();
  };

  return {
    methods,
    country,
    onCountryChange: setCountry,
    isSupplierAsUser,
    onIsSupplierAsUserChange: setIsSupplierAsUser,
    canSave,
    isSaving,
    onSave: handleSubmit(onSubmit),
    onCancel
  };
};
